import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Plus, ArrowUpRight, ArrowDownLeft, Wallet as WalletIcon, IndianRupee, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { formatINR, formatDate } from "../../lib/utils.fmt";
import { toast } from "sonner";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import { openRazorpayCheckout, fetchRazorpayOrder } from "../../lib/razorpay";

const presetAmounts = [200, 500, 1000, 2000];

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState({ balance: 0 });
  const [txns, setTxns] = useState([]);
  // Recharges paid at the gateway but not yet confirmed to us — shown as
  // "being confirmed" so a slow confirmation never reads as a failure.
  const [pendingRecharges, setPendingRecharges] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(500);
  const [busy, setBusy] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoadError(null);
    return api.get("/api/customer/wallet").then(({ data }) => {
      setWallet(data.wallet); setTxns(data.transactions);
      setPendingRecharges(data.pending_recharges || []);
    }).catch((e) => setLoadError(friendlyMessage(e)));
  };
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return <LoginRequired title="Sign in to access your wallet" description="Recharge once, auto-debit on every delivery. Track every paisa." />;
  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load your wallet" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  const recharge = async () => {
    if (amount <= 0) return;
    setBusy(true);
    try {
      const order = await fetchRazorpayOrder(`/api/customer/wallet/razorpay-order?amount=${amount}`);
      if (order.mode === "mock") {
        await api.post("/api/customer/wallet/recharge", { amount: Number(amount), method: "mock" });
        toast.success(`₹${amount} added to wallet`);
        setOpen(false);
        load();
        return;
      }
      try {
        const res = await openRazorpayCheckout({
          key: order.key_id,
          amount: order.amount_paise,
          currency: order.currency,
          order_id: order.order_id,
          name: "Wallet recharge",
          description: `Add ₹${amount} to wallet`,
        });
        try {
          const { data } = await api.post("/api/customer/wallet/recharge", {
            amount: Number(amount), method: "razorpay",
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_signature: res.razorpay_signature,
          });
          // 202 "pending": Razorpay has the payment but has not captured it
          // yet (or could not be asked) — say it is being confirmed; the
          // server credits it once captured. Only a credited answer says "added".
          if (data?.pending) toast.success(data.message || "Payment received — it is being confirmed and will appear in your wallet automatically.");
          else toast.success(`₹${amount} added to wallet`);
        } catch (err) {
          const status = err?.response?.status;
          if (status && status >= 400 && status < 500) {
            // The server looked at this payment and refused it (e.g. it
            // belongs to another account) — say so, don't promise a credit.
            toast.error(err.response?.data?.detail || "This payment could not be added to your wallet.");
          } else {
            // The PAYMENT went through (checkout resolved) — only our
            // confirmation call didn't reach the server (network / server
            // error). The server settles it from the payment record; never
            // tell the customer it failed.
            toast.success("Payment received — your wallet will be updated automatically in a few minutes.");
          }
        }
        setOpen(false);
        load();
      } catch (e) {
        if (e?.cancelled) { /* user closed checkout — no toast */ }
        else toast.error(e?.description || e?.response?.data?.detail || "Payment failed");
      }
    } catch (e) { toast.error(e?.description || e?.response?.data?.detail || "Recharge failed"); } finally { setBusy(false); }
  };

  return (
    <div className="page-enter" data-testid="wallet-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/home" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Wallet</h1>
        </div>
      </header>

      <main className="px-5">
        <div className="rounded-3xl bg-brand-ink text-white p-6 relative overflow-hidden" data-testid="wallet-balance-card">
          <div className="absolute -right-12 -top-12 w-48 h-48 bg-brand-sky/30 blur-3xl rounded-full" />
          <div className="relative">
            <div className="text-xs uppercase tracking-wider font-bold text-white/60 flex items-center gap-1.5">
              <WalletIcon className="w-3.5 h-3.5"/> Available Balance
            </div>
            <div className="font-heading text-5xl font-extrabold mt-3 tracking-tight">{formatINR(wallet.balance)}</div>
            {pendingRecharges.length > 0 && (
              <div className="mt-3 rounded-2xl bg-white/10 px-3 py-2 text-xs font-bold inline-flex items-center gap-2" data-testid="pending-recharge-note">
                <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
                {formatINR(pendingRecharges.reduce((a, p) => a + (p.amount || 0), 0))} payment is being confirmed — it will appear here automatically.
              </div>
            )}
            <Button onClick={() => setOpen(true)}
                    className="mt-5 bg-white text-brand-ink hover:bg-white/90 rounded-2xl h-12 px-5 font-bold"
                    data-testid="open-recharge-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Money
            </Button>
          </div>
        </div>

        <h3 className="font-heading font-bold mt-6 mb-3">Transactions</h3>
        <div className="bg-white rounded-3xl border border-neutral-200 divide-y divide-neutral-100">
          {txns.length === 0 && (
            <EmptyState icon={WalletIcon} size="sm" title="No transactions yet"
                        description="Your recharges and deductions will show up here." testId="wallet-empty" />
          )}
          {txns.map((t) => {
            const credit = t.amount > 0;
            return (
              <div key={t.id} className="p-4 flex items-center gap-3" data-testid={`txn-${t.id}`}>
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${credit ? "bg-brand-leaf/10" : "bg-brand-clay/10"}`}>
                  {credit ? <ArrowDownLeft className="w-5 h-5 text-brand-leaf" /> : <ArrowUpRight className="w-5 h-5 text-brand-clay" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{t.description}</div>
                  <div className="text-xs text-neutral-500 capitalize">{t.type.replace("_", " ")} · {formatDate(t.created_at)}</div>
                </div>
                <div className={`font-mono font-bold ${credit ? "text-brand-leaf" : "text-brand-clay"}`}>
                  {credit ? "+" : ""}{formatINR(t.amount)}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="recharge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Add Money</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {presetAmounts.map((a) => (
              <button key={a} onClick={() => setAmount(a)}
                      className={`h-12 rounded-2xl font-bold text-sm border-2 ${amount === a ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200"}`}
                      data-testid={`preset-${a}`}>
                ₹{a}
              </button>
            ))}
          </div>
          <div className="relative mt-2">
            <IndianRupee className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   className="h-14 pl-12 rounded-2xl text-lg font-mono" data-testid="amount-input" />
          </div>
          <DialogFooter>
            <Button onClick={recharge} disabled={busy}
                    className="w-full h-14 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold text-base"
                    data-testid="confirm-recharge-btn">
              {busy ? "Processing…" : `Pay ₹${amount}`}
            </Button>
          </DialogFooter>
          <div className="text-xs text-neutral-500 text-center -mt-2">
            Secured by Razorpay
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
